import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';

const Dropdown = ({ 
  items = [], 
  onSelect, 
  placeholder = 'Search...',
  allowCustomValues = true,
  value = '',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value);
  const dropdownRef = useRef(null);
  
  // Ensure items is always an array
  const itemsArray = Array.isArray(items) ? items : [];

  // Update searchTerm when value prop changes
  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        
        // If we allow custom values, call onSelect with the current search term when dropdown closes
        if (allowCustomValues && searchTerm && searchTerm !== value) {
          onSelect(searchTerm);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [allowCustomValues, onSelect, searchTerm, value]);

  const filteredItems = itemsArray.filter(item => 
    String(item).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (item) => {
    onSelect(item);
    setSearchTerm(item);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    // Only call onSelect if we're allowing immediate updates
    if (allowCustomValues) {
      onSelect(newValue);
    }
  };

  const handleKeyDown = (e) => {
    // When user presses Enter, treat the current input as selection
    if (e.key === 'Enter') {
      setIsOpen(false);
      onSelect(searchTerm);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => !disabled && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className={`w-full px-3 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-gray-800 ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          placeholder={placeholder}
          disabled={disabled}
        />
        <Search className="absolute right-2 top-2 text-gray-400" size={20} />
      </div>
      
      {isOpen && filteredItems.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg">
          <ul className="py-1 max-h-48 overflow-auto">
            {filteredItems.map((item, index) => (
              <li
                key={index}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleSelect(item)}
              >
                {String(item)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Dropdown;